import { Link } from "@remix-run/react";
import { useState } from "react";

import { Button } from "~/components/ui/button";
import { useAutomaticSessionPrompt } from "~/context/automatic-session-prompt-context";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/components/ui/dialog";
import { useBillingEntitlement } from "~/hooks/queries/use-billing-entitlement";
import { useIsPractitioner } from "~/lib/user";

const BILLING_WARNING_SESSION_STORAGE_KEY =
    "billing-warning-modal-shown-this-session";

function isWarningOrHigher(thresholdState: string): boolean {
    return (
        thresholdState === "warning_80" ||
        thresholdState === "warning_100" ||
        thresholdState === "blocked"
    );
}

export function BillingSessionWarningModal() {
    const { data, isLoading: isLoadingEntitlement } = useBillingEntitlement();
    const { isPractitioner, isLoading: isLoadingPractitioner } =
        useIsPractitioner();
    const [isDismissed, setIsDismissed] = useState(false);

    const eligible =
        !isDismissed &&
        !isLoadingEntitlement &&
        !isLoadingPractitioner &&
        isPractitioner &&
        !!data?.enforced &&
        !!data.entitlement &&
        isWarningOrHigher(data.entitlement.thresholdState) &&
        (typeof window === "undefined" ||
            window.sessionStorage.getItem(
                BILLING_WARNING_SESSION_STORAGE_KEY,
            ) !== "true");
    const isOpen = useAutomaticSessionPrompt({
        id: "billing-session-warning",
        priority: 100,
        eligible,
    });

    const dismiss = () => {
        window.sessionStorage.setItem(
            BILLING_WARNING_SESSION_STORAGE_KEY,
            "true",
        );
        setIsDismissed(true);
    };

    if (!data?.enforced || !data.entitlement) {
        return null;
    }

    const units =
        data.entitlement.metricType === "client" ? "clients" : "sessions";

    return (
        <Dialog
            analyticsId="billing-session-warning-modal"
            open={isOpen}
            onOpenChange={(open) => {
                if (!open) dismiss();
            }}
        >
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        You&apos;re approaching your session limit
                    </DialogTitle>
                    <DialogDescription>
                        You&apos;ve used {data.entitlement.usedUnits} of{" "}
                        {data.entitlement.limitUnits} {units} included in your
                        plan this period. If you&apos;d like more sessions, you
                        can upgrade your plan at any time.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="outline" onClick={dismiss}>
                        Dismiss
                    </Button>
                    <Button asChild onClick={dismiss}>
                        <Link to="/account/membership">Upgrade your plan</Link>
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
